import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { FaroInsightsWrapper } from "@/grafana-faro"
import { CustomSofiaEvent, sofiaEventDispatch } from "@/hooks/useSofiaEventListener"
import { toast } from "@/hooks/useToast"
import { uuidv4 } from "@/lib/generate-uuidv4"
import { showAIErrorMessages } from "@/modules/chats/lib/show-ai-error-messages"
import { AiResult } from "@/types/__generated__/union-types"

type AiFeatureContext = "CHAT" | "ANAMNESIS"

/** Fields every AI-feature result exposes — used by the shared lifecycle (success gate + Faro). */
type AiFeatureMeta = {
  result: AiResult
  fileHash: string
  aiRequestDurationMs: number
  aiAnonymizationRequestDurationMs: number
}

type RunAiFeatureParams<TResponse, TMeta extends AiFeatureMeta> = {
  /** CHAT vs ANAMNESIS — recorded in telemetry; callers branch their own small differences off it. */
  context: AiFeatureContext
  /** Reflect "this feature is running" in the caller's state (chat store or local useState). */
  start: () => void
  /** Reset the caller's running state (typically back to the idle state). Always runs in `finally`. */
  stop: () => void
  faro: { event: string; feature: string }
  /** Run the AI query. Receives the generated `aiFeatureId`. Return null/undefined to no-op. */
  run: (aiFeatureId: string) => Promise<TResponse | null | undefined>
  /** Pull the result envelope (result code + Faro fields) out of the query response. */
  selectMeta: (response: TResponse) => TMeta
  /** Apply a successful result (write to the composer, anamnesis form, suggestion chips, …). */
  onSuccess: (meta: TMeta, aiFeatureId: string) => void
  /** Optional recovery on thrown error (e.g. restore the previous text, log). */
  onError?: (error: unknown) => void
}

/**
 * Centralizes the identical AI-feature run lifecycle shared by the chat composer and the
 * progress-note anamnesis section: set running state, dispatch the global running event,
 * generate the feature id, run the query, gate on SUCCESS (else `showAIErrorMessages`),
 * push the Faro insight, toast + recover on throw, and always reset.
 *
 * Per-feature/per-context specifics (which query, where the text goes, flavour, Faro labels)
 * are supplied by the caller, so no boilerplate is duplicated.
 */
export const useRunAiFeature = () => {
  const { t } = useTranslation()

  return useCallback(
    async function runAiFeature<TResponse, TMeta extends AiFeatureMeta>({
      context,
      start,
      stop,
      faro,
      run,
      selectMeta,
      onSuccess,
      onError,
    }: RunAiFeatureParams<TResponse, TMeta>) {
      start()
      sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", true))

      const aiFeatureId = uuidv4()

      try {
        const response = await run(aiFeatureId)
        if (!response) {
          return
        }

        const meta = selectMeta(response)
        if (meta.result === "SUCCESS") {
          onSuccess(meta, aiFeatureId)

          FaroInsightsWrapper.pushEvent(faro.event, {
            aiFeatureId,
            context,
            feature: faro.feature,
            fileHash: meta.fileHash,
            aiRequestDurationMs: meta.aiRequestDurationMs.toString(),
            aiAnonymizationRequestDurationMs: meta.aiAnonymizationRequestDurationMs.toString(),
            sentAt: new Date().toISOString(),
          })
        } else {
          showAIErrorMessages(meta.result)
        }
      } catch (error) {
        toast({ description: t("chat.ai.errors.UNKNOWN_ERROR") })
        onError?.(error)
      } finally {
        stop()
        sofiaEventDispatch(CustomSofiaEvent("AI_FEATURE_RUNNING_EVENT", false))
      }
    },
    [t],
  )
}
